import React, { useState, useEffect } from "react";
import PhoneInputLib from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { CountryData } from "react-phone-input-2";
// [2026-02-07] Import phone validator for libphonenumber-js validation
import { processPhoneNumber } from "../../utils/phoneValidator";

interface PhoneInputProps {
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  isInvalid?: boolean;
  country?: string; // [2026-02-06] Country name to sync with country dropdown
  onValidationChange?: (isValid: boolean) => void; // [2026-02-07] Callback for validation state
}

// [2026-02-06] Map country names to country codes
const countryNameToCode: Record<string, string> = {
  "united states": "us", "usa": "us", "united kingdom": "gb", "uk": "gb",
  "israel": "il", "germany": "de", "france": "fr", "italy": "it", "spain": "es",
  "canada": "ca", "australia": "au", "new zealand": "nz", "japan": "jp",
  "china": "cn", "south korea": "kr", "india": "in", "brazil": "br",
  "mexico": "mx", "argentina": "ar", "netherlands": "nl", "belgium": "be",
  "switzerland": "ch", "austria": "at", "sweden": "se", "norway": "no",
  "denmark": "dk", "finland": "fi", "poland": "pl",
  // [2026-02-07] BR-27: Russia removed - restricted country
  "ukraine": "ua", "south africa": "za", "egypt": "eg", "united arab emirates": "ae",
  "saudi arabia": "sa", "turkey": "tr", "greece": "gr", "portugal": "pt",
  "ireland": "ie", "singapore": "sg", "hong kong": "hk", "taiwan": "tw",
  "thailand": "th", "malaysia": "my", "philippines": "ph", "indonesia": "id",
  "vietnam": "vn", "pakistan": "pk", "bangladesh": "bd", "nigeria": "ng",
  "kenya": "ke", "ghana": "gh",
};

// Country dial codes mapping
const getDialCode = (countryCode: string): string => {
  const dialCodes: Record<string, string> = {
    us: "1", gb: "44", il: "972", de: "49", fr: "33", it: "39", es: "34",
    ca: "1", au: "61", nz: "64", jp: "81", cn: "86", kr: "82", in: "91",
    br: "55", mx: "52", ar: "54", nl: "31", be: "32", ch: "41", at: "43",
    se: "46", no: "47", dk: "45", fi: "358", pl: "48", ua: "380",
    // [2026-02-07] BR-27: Russia (ru: "7") removed - restricted country
    za: "27", eg: "20", ae: "971", sa: "966", tr: "90", gr: "30", pt: "351",
    ie: "353", sg: "65", hk: "852", tw: "886", th: "66", my: "60", ph: "63",
    id: "62", vn: "84", pk: "92", bd: "880", ng: "234", ke: "254", gh: "233",
  };
  return dialCodes[countryCode.toLowerCase()] || "1";
};

// Phone number masks by country (for local number only, without country code)
// X = digit placeholder
const getPhoneMask = (countryCode: string): string => {
  const masks: Record<string, string> = {
    us: "(XXX) XXX-XXXX",
    ca: "(XXX) XXX-XXXX",
    gb: "XXXX XXX XXXX",
    il: "XX-XXX-XXXX",
    de: "XXXX XXXXXXX",
    fr: "X XX XX XX XX",
    it: "XXX XXX XXXX",
    es: "XXX XX XX XX",
    au: "XXX XXX XXX",
    nz: "XX XXX XXXX",
    jp: "XX-XXXX-XXXX",
    cn: "XXX XXXX XXXX",
    kr: "XX-XXXX-XXXX",
    in: "XXXXX XXXXX",
    br: "(XX) XXXXX-XXXX",
    mx: "XX XXXX XXXX",
    nl: "X XX XX XX XX",
    be: "XXX XX XX XX",
    ch: "XX XXX XX XX",
    at: "XXX XXXXXXX",
    se: "XX-XXX XX XX",
    no: "XXX XX XXX",
    dk: "XX XX XX XX",
    pl: "XXX XXX XXX",
    // [2026-02-07] BR-27: Russia mask removed - restricted country
    ae: "XX XXX XXXX",
    sa: "XX XXX XXXX",
    tr: "XXX XXX XX XX",
    za: "XX XXX XXXX",
    sg: "XXXX XXXX",
    hk: "XXXX XXXX",
  };
  return masks[countryCode.toLowerCase()] || "XXX XXX XXXX";
};

// Format number according to mask
// [2026-02-13] FIX_PHONE_MAX_LENGTH: overflow digits separated by space instead of raw append
const formatWithMask = (digits: string, mask: string): string => {
  if (!digits) return "";
  let result = "";
  let digitIndex = 0;

  for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
    if (mask[i] === "X") {
      result += digits[digitIndex];
      digitIndex++;
    } else {
      result += mask[i];
    }
  }

  // Append remaining digits with a space separator (for variable-length numbers)
  if (digitIndex < digits.length) {
    result += " " + digits.slice(digitIndex);
  }

  return result;
};

// Get placeholder from mask
const getPlaceholder = (mask: string): string => {
  return mask.replace(/X/g, "_");
};

// Extract phone number without country code
const extractLocalNumber = (fullNumber: string, dialCode: string): string => {
  if (!fullNumber) return "";
  const digits = fullNumber.replace(/\D/g, "");
  if (digits.startsWith(dialCode)) {
    return digits.slice(dialCode.length);
  }
  return digits;
};

// Detect country from full phone number
const detectCountryFromNumber = (fullNumber: string): string => {
  if (!fullNumber) return "us";
  const digits = fullNumber.replace(/\D/g, "");

  // Check longer codes first (3 digits, then 2, then 1)
  // [2026-02-07] BR-27: Removed restricted countries (Russia, Iraq, Iran, Yemen, North Korea)
  const countryCodes: [string, string][] = [
    ["972", "il"], ["971", "ae"], ["966", "sa"], ["965", "kw"],
    ["963", "sy"], ["962", "jo"], ["961", "lb"], ["960", "mv"], ["880", "bd"],
    ["852", "hk"], ["886", "tw"], ["855", "kh"], ["856", "la"], ["358", "fi"],
    ["353", "ie"], ["351", "pt"], ["380", "ua"], ["234", "ng"], ["254", "ke"],
    ["233", "gh"], ["91", "in"], ["90", "tr"], ["86", "cn"], ["82", "kr"],
    ["81", "jp"], ["66", "th"], ["65", "sg"], ["64", "nz"], ["63", "ph"],
    ["62", "id"], ["61", "au"], ["60", "my"], ["58", "ve"], ["57", "co"],
    ["56", "cl"], ["55", "br"], ["54", "ar"], ["53", "cu"], ["52", "mx"],
    ["51", "pe"], ["49", "de"], ["48", "pl"], ["47", "no"], ["46", "se"],
    ["45", "dk"], ["44", "gb"], ["43", "at"], ["41", "ch"], ["40", "ro"],
    ["39", "it"], ["36", "hu"], ["34", "es"], ["33", "fr"], ["32", "be"],
    ["31", "nl"], ["30", "gr"], ["27", "za"], ["20", "eg"],
    ["1", "us"],
  ];

  for (const [code, country] of countryCodes) {
    if (digits.startsWith(code)) {
      return country;
    }
  }
  return "us";
};

export const PhoneInput: React.FC<PhoneInputProps> = ({
  name,
  value,
  onChange,
  onBlur,
  isInvalid,
  country,
  onValidationChange,
}) => {
  // Detect initial country from value or default to US
  const [selectedCountry, setSelectedCountry] = useState(() =>
    value ? detectCountryFromNumber(value) : "us"
  );
  const [dialCode, setDialCode] = useState(() => getDialCode(selectedCountry));
  const [localNumber, setLocalNumber] = useState(() =>
    extractLocalNumber(value, dialCode)
  );
  // [2026-02-07] Validation state using libphonenumber-js
  const [validationError, setValidationError] = useState<string | null>(null);
  const [hasBlurred, setHasBlurred] = useState(false);

  // [2026-02-06] Sync country when country prop changes (from country dropdown)
  // [2026-02-13] FIX_PHONE_PERSIST: Skip onChange when localNumber is empty (initial load)
  // to prevent overwriting phone value with just the dial code
  useEffect(() => {
    if (country) {
      const countryCode = countryNameToCode[country.toLowerCase()];
      if (countryCode && countryCode !== selectedCountry) {
        const newDialCode = getDialCode(countryCode);
        setSelectedCountry(countryCode);
        setDialCode(newDialCode);
        // Only update parent if user already entered a local number;
        // during initial form load localNumber is "" and the value effect handles it
        if (localNumber) {
          onChange(`+${newDialCode}${localNumber}`);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  // Update local number when external value changes
  // [2026-02-13] FIX_PHONE_PERSIST: Detect country from value itself to avoid
  // stale selectedCountry causing incorrect extraction on initial form load
  useEffect(() => {
    if (!value || value.replace(/\D/g, "").length === 0) {
      if (localNumber) setLocalNumber("");
      return;
    }
    const detected = detectCountryFromNumber(value);
    const detectedDial = getDialCode(detected);
    const extracted = extractLocalNumber(value, detectedDial);
    // Sync country/dialCode if the value indicates a different country
    if (detected !== selectedCountry) {
      setSelectedCountry(detected);
      setDialCode(detectedDial);
    }
    if (extracted !== localNumber) {
      setLocalNumber(extracted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Handle country selection from flag dropdown
  const handleCountryChange = (_value: string, data: CountryData | {}) => {
    if ('dialCode' in data && 'countryCode' in data) {
      const newDialCode = data.dialCode;
      const newCountry = data.countryCode;
      setSelectedCountry(newCountry);
      setDialCode(newDialCode);
      // Update parent with new country code + existing local number
      onChange(`+${newDialCode}${localNumber}`);
    }
  };

  // Get current mask based on selected country
  const currentMask = getPhoneMask(selectedCountry);

  // [2026-02-13] FIX_PHONE_MAX_LENGTH: E.164 max is 15 digits total (incl. country code)
  const maxLocalDigits = 15 - dialCode.length;

  // Handle phone number input (local number only)
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    // Extract only digits from input, enforce max length
    const digitsOnly = inputVal.replace(/\D/g, "").slice(0, maxLocalDigits);
    setLocalNumber(digitsOnly);
    // Combine with country code and pass to parent (store raw digits)
    onChange(`+${dialCode}${digitsOnly}`);
  };

  // Format the display value with mask
  const displayValue = formatWithMask(localNumber, currentMask);

  // [2026-02-07] Validate phone number using libphonenumber-js
  const validatePhone = (phoneValue: string, countryCode: string) => {
    // Skip validation if empty (let form validation handle required fields)
    if (!phoneValue || phoneValue.replace(/\D/g, "").length === 0) {
      setValidationError(null);
      onValidationChange?.(true);
      return;
    }

    const result = processPhoneNumber(phoneValue, countryCode.toUpperCase());
    if (result.isValid) {
      setValidationError(null);
      onValidationChange?.(true);
    } else {
      setValidationError("Invalid phone number");
      onValidationChange?.(false);
    }
  };

  // [2026-02-07] Handle blur with validation
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setHasBlurred(true);
    const fullNumber = `+${dialCode}${localNumber}`;
    validatePhone(fullNumber, selectedCountry);
    onBlur?.(e);
  };

  // Show error if validation failed after blur, or if parent says invalid
  const showError = isInvalid || (hasBlurred && validationError);

  return (
    <div className="phone-input-container">
      <div className={`phone-input-wrapper phone-input-split ${showError ? "is-invalid" : ""}`}>
        {/* Flag dropdown only */}
        <div className="phone-flag-selector">
          <PhoneInputLib
            country={selectedCountry}
            value=""
            onChange={handleCountryChange}
            enableSearch={true}
            disableSearchIcon={true}
            inputProps={{
              readOnly: true,
              className: "phone-hidden-input",
            }}
            containerClass="phone-flag-container"
            buttonClass="phone-input-flag-button"
            dropdownClass="phone-input-dropdown"
          />
        </div>

        {/* Country code display (read-only) */}
        <div className="phone-country-code">+{dialCode}</div>

        {/* Phone number input (editable) with mask */}
        <input
          type="tel"
          name={name}
          value={displayValue}
          onChange={handleNumberChange}
          onBlur={handleBlur}
          className={`form-control field-input phone-number-input ${showError ? "is-invalid" : ""}`}
          placeholder={getPlaceholder(currentMask)}
        />
      </div>
      {/* [2026-02-07] Show validation error message */}
      {hasBlurred && validationError && (
        <div className="invalid-feedback d-block">{validationError}</div>
      )}
    </div>
  );
};

export default PhoneInput;
